import React from 'react';
import PropTypes from 'prop-types';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useSelector } from 'react-redux';
import { selectCurrentVenue } from '../../../../../redux/slices/currentUserSlice';
import { getVenueTimezone } from '../../../../../utils/venueTimeFormatters';
import dayjs from '../../../../../utils/dayjsConfig';

import { CustomDateTimePicker } from '../ShiftTimeEntry.styles';

/**
 * Interactive time picker component for editing shift times
 * Used for future shifts or when modifying unlogged times
 *
 * Uses venue timezone consistently for both display and picker
 */
const ShiftTimePicker = ({ scheduledTime, isPickerOpen, status, disabled, onTimeChange, setOpenPicker }) => {
  const currentVenue = useSelector(selectCurrentVenue);
  const venueTimezone = getVenueTimezone(currentVenue);

  // Convert UTC time to venue timezone using Day.js
  const getVenueDisplayTime = (utcTime) => {
    if (!utcTime) return null;

    // Ensure input is interpreted as UTC, then convert to venue timezone
    return dayjs(utcTime).utc().tz(venueTimezone);
  };

  // Convert venue time back to UTC for storage
  const getUTCTime = (venueTime) => {
    if (!venueTime) return null;

    // Convert venue time back to UTC
    return dayjs(venueTime).utc().toDate();
  };

  const handleTimeChange = (newTime) => {
    if (disabled) return;
    // Convert the selected venue time back to UTC for storage
    const utcTime = getUTCTime(newTime);
    onTimeChange(utcTime);
  };

  // Use Day.js adapter with timezone support
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <CustomDateTimePicker
        value={getVenueDisplayTime(scheduledTime)}
        timezone={venueTimezone}
        open={!disabled && isPickerOpen}
        isCompleted={status === 'completed'}
        views={['day', 'hours', 'minutes']}
        onAccept={handleTimeChange}
        disabled={disabled}
        disablePast={false} // This allows users to select the right time before selecting AM/PM; validation occurs elsewhere
        format="h:mm a"
        onClose={() => setOpenPicker(false)}
        slots={{ openPickerIcon: () => null }}
        PopperProps={{
          placement: 'bottom-start',
          disablePortal: false,
        }}
        slotProps={{
          field: {
            shouldRespectLeadingZeros: true,
          },
          textField: {
            onClick: disabled ? null : () => setOpenPicker(true),
            sx: {
              opacity: disabled ? 0.5 : 1,
              cursor: disabled ? 'not-allowed' : 'pointer',
              '& .MuiInputBase-input': {
                cursor: disabled ? 'not-allowed' : 'pointer',
              },
            },
          },
          digitalClockSectionItem: {
            sx: {
              '&:hover': {
                backgroundColor: (theme) => theme.palette.background.lightHover,
              },
              '&.Mui-selected': {
                backgroundColor: (theme) => theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.text.secondaryHover,
                },
              },
            },
          },
        }}
      />
    </LocalizationProvider>
  );
};

ShiftTimePicker.propTypes = {
  scheduledTime: PropTypes.instanceOf(Date),
  isPickerOpen: PropTypes.bool.isRequired,
  status: PropTypes.string,
  disabled: PropTypes.bool,
  onTimeChange: PropTypes.func.isRequired,
  setOpenPicker: PropTypes.func.isRequired,
};

export default ShiftTimePicker;
